<template>
  <div>
    <!-- Loading Overlay -->
    <div
      v-if="loading"
      class="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
    >
      <div class="bg-white p-6 rounded-lg flex flex-col items-center">
        <svg
          class="animate-spin h-10 w-10 text-[#E87055] mb-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8H4z"
          ></path>
        </svg>
        <span class="text-[#E87055] font-semibold">Logging in...</span>
      </div>
    </div>

    <div
      class="min-h-screen flex items-center justify-center px-4 py-8 bg-gradient-to-br from-white to-[#fde6b8]"
    >
      <!-- Container -->
      <div
        class="flex flex-col md:flex-row w-full max-w-4xl shadow-lg rounded-lg overflow-hidden bg-white"
      >
        <!-- Top / Left Panel: Branding -->
        <div
          class="relative w-full md:w-1/2 bg-[#E87055] text-white flex flex-col items-center justify-center px-6 py-8 md:p-12 overflow-hidden"
        >
          <!-- Background Logo -->
          <img
            :src="IWCLogo"
            alt=""
            class="absolute inset-0 m-auto w-72 md:w-96 opacity-20 pointer-events-none select-none"
          />

          <!-- Version -->
          <span
            class="absolute top-3 left-4 text-sm opacity-80 select-none hidden md:block"
          >
            Version 1.0.0
          </span>

          <!-- Foreground content -->
          <h1 class="text-2xl md:text-4xl font-bold md:mb-4 z-10 select-none">
            Gazette CMS
          </h1>
          <span class="text-sm opacity-80 select-none mb-2 block lg:hidden">
            Version 1.1.0
          </span>
          <p class="text-center text-sm md:text-base max-w-xs z-10 select-none">
            Welcome to the CMS. Please login to continue.
          </p>
        </div>

        <!-- Login Panel -->
        <div
          class="w-full md:w-1/2 px-6 py-10 md:p-14 flex flex-col justify-start md:justify-center"
        >
          <h2
            class="text-2xl md:text-3xl text-[#2F2F2F] font-semibold text-center mb-10 select-none"
          >
            Admin Login
          </h2>

          <form @submit.prevent="handleLogin" class="space-y-8 text-[#2F2F2F]">
            <!-- Username -->
            <div class="space-y-2">
              <label class="block text-sm font-medium" for="username">
                Username
              </label>
              <input
                id="username"
                type="text"
                v-model="username"
                placeholder="Enter username"
                class="w-full border-b-2 border-gray-300 bg-transparent px-0 py-2 focus:outline-none focus:border-[#E87055]"
                required
              />
            </div>

            <!-- Password -->
            <div class="space-y-2">
              <label class="block text-sm font-medium" for="password">
                Password
              </label>
              <input
                id="password"
                type="password"
                v-model="password"
                placeholder="Enter password"
                class="w-full border-b-2 border-gray-300 bg-transparent px-0 py-2 focus:outline-none focus:border-[#E87055]"
                required
              />
            </div>

            <!-- Submit -->
            <button
              type="submit"
              @click="goToContent"
              class="w-full bg-[#E87055] text-white py-3 rounded hover:bg-[#d95c41] transition"
            >
              Login
            </button>
          </form>

          <p v-if="error" class="text-red-500 text-sm mt-6 text-center">
            {{ error }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";
import { useRouter } from "vue-router";
import { loginUser } from "@/api/authApi";
import IWCLogo from "@/assets/iwc_logo.png";
import { useStore } from "vuex";

export default {
  setup() {
    const username = ref("");
    const password = ref("");
    const error = ref("");
    const loading = ref(false);
    const router = useRouter();

    const store = useStore();

    const handleLogin = async () => {
      error.value = "";
      loading.value = true;

      try {
        const payload = {
          username: username.value,
          password: password.value,
        };

        // loginUser now returns { token, user } only for allowed users
        const { token, user } = await loginUser(payload);

        // Save token & user info only if allowed
        localStorage.setItem("token", token);
        store.commit("auth/SET_TOKEN", token);

        localStorage.setItem("user", JSON.stringify(user));
        store.commit("auth/SET_USER", user);

        localStorage.setItem("gazetteRole", user.gazetteRole);

        router.push("/home");
      } catch (err) {
        console.error("Login error:", err.response || err);

        // Unauthorized user (or other errors)
        error.value =
          err.code === 403
            ? err.message
            : err.response?.data?.message || "Login failed. Please try again.";
      } finally {
        loading.value = false;
      }
    };

    return {
      error,
      loading,
      IWCLogo,
      username,
      password,

      handleLogin,
    };
  },
};
</script>
